import React, { useState, useEffect } from 'react';
import './PartenairesPage.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { toSentenceCase } from '../utils/textUtils';
import useSEO from '../hooks/useSEO';

function PartenairesPage() {
  useSEO({
    title: "Réseau de Partenaires & Entreprises Associées | GSU Les Palmiers",
    description: "Découvrez notre écosystème de partenaires institutionnels, universitaires et industriels soutenant l'excellence pédagogique du GSU Les Palmiers à Togba.",
    keywords: "partenaires GSU Les Palmiers, réseau entreprises Abomey-Calavi, coopération académique Bénin, partenaires école Togba",
    canonicalUrl: "https://gsulespalmiers.bj/partenaires"
  });

  const [partenaires, setPartenaires] = useState([]);
  const [filtreType, setFiltreType] = useState('tous');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const typesPartenariat = {
    'tous': 'Tous les partenaires',
    'entreprise': 'Entreprises',
    'institution': 'Institutions',
    'ong': 'ONG',
    'gouvernement': 'Gouvernement',
    'international': 'International',
    'academique': 'Académique',
    'financier': 'Financier',
    'technique': 'Technique'
  };

  useEffect(() => {
    fetchPartenaires();
  }, []);

  const fetchPartenaires = async () => {
    try {
      setLoading(true);
      const response = await fetch('http://127.0.0.1:8000/api/v1/partenaires');
      const data = await response.json();
      
      if (response.ok) {
        setPartenaires(data.partenaires || []);
      } else {
        setError('Erreur lors du chargement des partenaires');
      }
    } catch (error) {
      console.error('Erreur:', error);
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const partenairesFiltrés = filtreType === 'tous' 
    ? partenaires 
    : partenaires.filter(p => p.type_partenariat === filtreType);

  const handlePartenaireClick = (partenaire) => {
    if (partenaire.site_web) {
      window.open(partenaire.site_web, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="partenaires-page">
        <Header activePage="partenaires" />
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Chargement des partenaires...</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="partenaires-page">
      <Header activePage="partenaires" />
      
      {/* Hero Section */}
      <section className="partenaires-hero">
        <div className="container">
          <div className="hero-content">
            <h1>Nos Partenaires</h1>
            <p>Découvrez les entreprises, institutions et organisations qui nous accompagnent dans notre mission éducative</p>
          </div>
        </div>
      </section>

      {/* Bouton Retour */}
      <div className="back-button-section">
        <div className="container">
          <button 
            className="btn-back"
            onClick={() => {
              if (window.navigateTo) {
                window.navigateTo('projets');
              } else {
                window.history.back();
              }
            }}
            aria-label="Retour"
          >
            ← Retour
          </button>
        </div>
      </div>

      {/* Filtres */}
      <section className="partenaires-filtres">
        <div className="container">
          <div className="filtres-container">
            {Object.entries(typesPartenariat).map(([type, label]) => (
              <button
                key={type}
                className={`filtre-btn ${filtreType === type ? 'active' : ''}`}
                onClick={() => setFiltreType(type)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Liste des Partenaires */}
      <section className="partenaires-liste">
        <div className="container">
          {error ? (
            <div className="error-message">
              <p>{error}</p>
              <button onClick={fetchPartenaires} className="retry-btn">
                Réessayer
              </button>
            </div>
          ) : partenairesFiltrés.length === 0 ? (
            <div className="empty-state">
              <h3>Aucun partenaire trouvé</h3>
              <p>
                {filtreType === 'tous' 
                  ? 'Aucun partenaire n\'est actuellement disponible.'
                  : `Aucun partenaire de type "${typesPartenariat[filtreType]}" n'est disponible.`
                }
              </p>
            </div>
          ) : (
            <>
              <div className="section-header">
                <h2>
                  {filtreType === 'tous' 
                    ? `Tous nos partenaires (${partenairesFiltrés.length})`
                    : `${typesPartenariat[filtreType]} (${partenairesFiltrés.length})`
                  }
                </h2>
              </div>
              
              <div className="partenaires-grid">
                {partenairesFiltrés.map((partenaire) => (
                  <div 
                    key={partenaire.id} 
                    className={`partenaire-card ${partenaire.site_web ? 'clickable' : ''}`}
                    onClick={() => handlePartenaireClick(partenaire)}
                  >
                    <div className="partenaire-logo">
                      {partenaire.logo ? (
                        <img 
                          src={`http://127.0.0.1:8000/storage/${partenaire.logo}`} 
                          alt={partenaire.nom}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div className="logo-placeholder" style={{display: partenaire.logo ? 'none' : 'flex'}}>
                        <span>{partenaire.nom.charAt(0)}</span>
                      </div>
                    </div>
                    
                    <div className="partenaire-info">
                      <h3>{toSentenceCase(partenaire.nom)}</h3>
                      <span className="partenaire-type">
                        {typesPartenariat[partenaire.type_partenariat] || toSentenceCase(partenaire.type_partenariat)}
                      </span>
                      
                      {partenaire.description && (
                        <p className="partenaire-description">
                          {partenaire.description}
                        </p>
                      )}
                      
                      <div className="partenaire-contact">
                        {partenaire.site_web && (
                          <div className="contact-item">
                            <span className="contact-icon">🌐</span>
                            <span>Site web</span>
                          </div>
                        )}
                        {partenaire.email && (
                          <div className="contact-item">
                            <span className="contact-icon">📧</span>
                            <span>{partenaire.email}</span>
                          </div>
                        )}
                        {partenaire.telephone && (
                          <div className="contact-item">
                            <span className="contact-icon">📞</span>
                            <span>{partenaire.telephone}</span>
                          </div>
                        )}
                      </div>
                      
                      {partenaire.date_debut && (
                        <div className="partenaire-dates">
                          <small>
                            Partenaire depuis {new Date(partenaire.date_debut).getFullYear()}
                            {partenaire.date_fin && (
                              ` - ${new Date(partenaire.date_fin).getFullYear()}`
                            )}
                          </small>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default PartenairesPage;